import React from 'react';
import { AlertTriangle, FileWarning, FolderOpen, ClipboardPaste, X } from 'lucide-react';
import { LoadErrorKind } from '../features/openPresentation/loadErrorInfo';

/**
 * The page you land on when a deck will not open.
 *
 * A file that fails to load used to produce an alert, a three-second toast, or
 * — worst of all — an empty editor that looked like the deck had opened and
 * lost everything. This is the replacement: it stays on screen, it says which
 * file failed and where, and it offers the two things anyone actually wants
 * next (pick a different file, or paste the JSON instead).
 *
 * It renders in two shapes:
 *   - `variant="page"` — the landing screen, where there is no deck behind it;
 *   - `variant="modal"` — inside the editor, where the deck you already had is
 *     still open behind the overlay and must not be disturbed.
 *
 * @param {object} props
 * @param {ReturnType<import('../features/openPresentation/loadErrorInfo').describeLoadError>} props.error
 * @param {'page' | 'modal'} [props.variant]
 * @param {() => void} [props.onRetry] - Reopen the file picker
 * @param {() => void} [props.onPasteInstead] - Switch to the paste-JSON route
 * @param {() => void} [props.onDismiss] - Close and carry on
 * @param {string} [props.dismissLabel]
 */
export default function PresentationLoadErrorScreen({
  error,
  variant = 'modal',
  onRetry,
  onPasteInstead,
  onDismiss,
  dismissLabel
}) {
  if (!error) return null;

  const Icon = error.kind === LoadErrorKind.INVALID_FORMAT ? FileWarning : AlertTriangle;
  const closeLabel = dismissLabel || (variant === 'page' ? 'Back to templates' : 'Keep my current deck');

  return (
    <div
      className={
        variant === 'page'
          ? 'fixed inset-0 z-[120] flex items-center justify-center bg-gray-950/90 p-4 overflow-y-auto'
          : 'fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4 overflow-y-auto'
      }
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="deck-load-error-title"
    >
      {/* Scrolls inside itself: the details list can outgrow a laptop screen,
          and the buttons at the bottom are the whole point of the panel. */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <div className="flex items-start gap-4 border-b border-gray-200 bg-red-50 px-6 py-5 rounded-t-2xl">
          <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
            <Icon size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="deck-load-error-title" className="text-xl font-semibold text-gray-900">
              {error.title}
            </h2>
            {error.fileName && (
              <p className="mt-1 truncate font-mono text-sm text-gray-600" title={error.fileName}>
                {error.fileName}
              </p>
            )}
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-lg p-2 text-gray-500 hover:bg-red-100 hover:text-gray-700"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className="space-y-5 px-6 py-5">
          <p className="text-sm leading-relaxed text-gray-700">{error.summary}</p>

          {error.location && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-900">
                {error.location.column
                  ? `Line ${error.location.line}, column ${error.location.column}`
                  : `Around line ${error.location.line}`}
              </p>
              {error.location.excerpt && (
                <pre className="mt-2 overflow-x-auto rounded bg-amber-100/70 px-3 py-2 text-xs text-amber-950">
                  <code>{error.location.excerpt}</code>
                </pre>
              )}
            </div>
          )}

          {error.issues?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">What is wrong with it</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                {error.issues.slice(0, 6).map((issue, index) => (
                  <li key={`${issue}-${index}`}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {error.suggestions?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">What to try</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                {error.suggestions.map((suggestion, index) => (
                  <li key={`${suggestion}-${index}`}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {error.detail && (
            <details className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Technical details
              </summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs text-gray-600">
                <code>{error.detail}</code>
              </pre>
            </details>
          )}
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="order-last rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 sm:order-first sm:mr-auto"
            >
              {closeLabel}
            </button>
          )}
          {onPasteInstead && (
            <button
              type="button"
              onClick={onPasteInstead}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              <ClipboardPaste size={16} />
              Paste JSON instead
            </button>
          )}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <FolderOpen size={16} />
              Choose another file
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
